<template>
  <div class="marketing-interactions-page inventory-root">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">[ Aftercare - Customers ]</h1>
      <div class="header-actions">
        <span class="record-count">{{ filteredTotal }} / {{ allInteractions.length }} Records</span>
        <v-btn color="primary" icon @click="loadData" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="filters-card">
      <div class="search-box">
        <v-icon class="search-icon">mdi-magnify</v-icon>
        <input
          v-model="searchText"
          type="text"
          placeholder="Search by name, email, order number, phone..."
          class="search-input"
          @input="filterData"
        />
      </div>

      <div class="filter-buttons">
        <button
          :class="['filter-btn', { active: statusFilter === null }]"
          @click="setStatusFilter(null)"
        >
          ● All
        </button>
        <button
          :class="['filter-btn', { active: statusFilter === 'active' }, 'badge-status', getStatusClass('active')]"
          @click="setStatusFilter('active')"
        >
          ● Active
        </button>
        <button
          :class="['filter-btn', { active: statusFilter === 'pending' }, 'badge-status', getStatusClass('pending')]"
          @click="setStatusFilter('pending')"
        >
          ● Pending
        </button>
        <button
          :class="['filter-btn', { active: statusFilter === 'in_progress' }, 'badge-status', getStatusClass('in_progress')]"
          @click="setStatusFilter('in_progress')"
        >
          ● In Progress
        </button>
        <button
          :class="['filter-btn', { active: statusFilter === 'delayed' }, 'badge-status', getStatusClass('delayed')]"
          @click="setStatusFilter('delayed')"
        >
          ● Delayed
        </button>
        <button
          :class="['filter-btn', { active: statusFilter === 'completed' }, 'badge-status', getStatusClass('completed')]"
          @click="setStatusFilter('completed')"
        >
          ● Completed
        </button>

        <div class="divider"></div>

        <button
          :class="['filter-btn', { active: ratingFilter === null }]"
          @click="setRatingFilter(null)"
        >
          All
        </button>
        <button
          :class="['filter-btn', { active: ratingFilter === 'yes' } , 'badge-rating', getRatingClass('yes')]"
          @click="setRatingFilter('yes')"
        >
          Send Rating
        </button>
        <button
          :class="['filter-btn', { active: ratingFilter === 'no' } , 'badge-rating', getRatingClass('no')]"
          @click="setRatingFilter('no')"
        >
          No Rating
        </button>
        <button
          :class="['filter-btn', { active: ratingFilter === 'not_decided' } , 'badge-rating', getRatingClass('not_decided')]"
          @click="setRatingFilter('not_decided')"
        >
          Not Decided
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4">Loading interactions...</p>
    </div>

    <!-- Data Table -->
    <div v-else-if="displayedData.length > 0" class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th class="col-index">#</th>
            <th class="sortable" @click="sortBy('order_number')">
              ORDER # 
              <v-icon small>{{ getSortIcon('order_number') }}</v-icon>
            </th>
            <th class="sortable" @click="sortBy('customer_name')">
              CUSTOMER 
              <v-icon small>{{ getSortIcon('customer_name') }}</v-icon>
            </th>
            <th class="sortable" @click="sortBy('order_date')">
              ORDER DATE 
              <v-icon small>{{ getSortIcon('order_date') }}</v-icon>
            </th>
            <th>CONTACT</th>
            <th class="text-center">PRODUCTS</th>
            <th class="sortable" @click="sortBy('contact_status')">
              STATUS 
              <v-icon small>{{ getSortIcon('contact_status') }}</v-icon>
            </th>
            <th class="sortable" @click="sortBy('callback_date')">
              CALL BACK DATE 
              <v-icon small>{{ getSortIcon('callback_date') }}</v-icon>
            </th>
            <th>RATING DECISION</th>
            <th>SALES PERSON</th>
            <th class="col-actions">OPS</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in displayedData" :key="item.id" class="data-row">
            <td class="col-index">{{ getRowNumber(index) }}</td>
            
            <td>
              <div class="order-cell">
                <v-icon small color="grey">mdi-cart-outline</v-icon>
                <span class="order-number">{{ item.order_number }}</span>
              </div>
            </td>

            <td>
              <div class="customer-cell">
                <div class="customer-name">{{ item.customer_name }}</div>
                <div class="customer-email">{{ item.customer_email }}</div>
              </div>
            </td>

            <td class="text-muted">{{ item.order_date }}</td>

            <td>
              <div class="contact-cell">
                <v-icon small>mdi-phone</v-icon>
                {{ item.customer_phone || 'N/A' }}
              </div>
            </td>

            <td class="text-center">
              <span class="badge badge-count">{{ item.products_count }}</span>
            </td>

            <td>
              <span :class="['badge', 'badge-status', getStatusClass(item.contact_status)]">
                ● {{ getStatusText(item.contact_status) }}
              </span>
            </td>

            <td class="text-muted">{{ item.callback_date || '-' }}</td>

            <td>
              <span :class="['badge', 'badge-rating', getRatingClass(item.rating_request_decision)]">
                {{ getRatingText(item.rating_request_decision) }}
              </span>
            </td>

            <td class="text-muted">{{ item.created_by || '-' }}</td>

            <td class="col-actions">
              <v-btn icon small @click="openView(item)">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn icon small disabled>
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <v-icon size="64" color="grey lighten-1">mdi-inbox-outline</v-icon>
      <p class="mt-4">No interactions found</p>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && filteredData.length > 0" class="pagination-card">
      <div class="pagination-info">
        Pages {{ currentPage }} / {{ totalPages }}
        <span class="mx-2">·</span>
        Rows per page:
        <select v-model.number="perPage" @change="handlePerPageChange" class="per-page-select">
          <option :value="5" selected>5</option>
          <option :value="10">10</option>
          <option :value="15">15</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="filteredData.length">All</option>
        </select>
      </div>

      <div class="pagination-controls">
        <button 
          class="page-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
        >
          <v-icon small>mdi-chevron-left</v-icon>
        </button>
        <button class="page-btn page-current">
          {{ currentPage }}
        </button>
        <button 
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
        >
          <v-icon small>mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top right>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-fade-transition>
      <AftercareViewDialog
        v-model="viewDialog"
        :item="selectedItem"
        :status-options="statusOptions"
        :call-status-options="callStatusOptions"
        :rating-options="ratingOptions"
        :satisfaction-options="satisfactionOptions"
        :internal-satisfaction="internalSatisfaction"
        @show-snackbar="showSnackbar"
        @save="saveInteraction"
        @completed="handleCompleted"
      />
    </v-fade-transition>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import AftercareViewDialog from "./components/AftercareViewDialog.vue"

export const ENDPOINT = "aftercare/";

export const initialFormData = () => ({
  sales_order_id: null,
  contact_status: null,
  call_status: null,
  call_date: null,
  callback_date: null,
  customer_satisfaction: null,
  general_notes: null,
  rating_request_decision: null,
  rating_request_sent: false,
  internal_rating: null,
  completed_at: null,
  created_by: null,
  assigned_to: null,
});

export default {
  name: 'Aftercare',
  components: { AftercareViewDialog },

  data() {
    return {
      // Raw data
      allInteractions: [],
      
      // Filters
      searchText: '',
      statusFilter: 'active',
      ratingFilter: null,
      
      // Sorting
      sortField: 'created_at',
      sortDirection: 'desc',
      
      // Pagination
      currentPage: 1,
      perPage: 5,
      
      // UI state
      loading: false,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      form: initialFormData(),
      viewDialog: false,
      selectedItem: null,

      statusOptions: [
        { value: 'pending', text: 'Pending' },
        { value: 'in_progress', text: 'In Progress' },
        { value: 'delayed', text: 'Delayed' },
        { value: 'completed', text: 'Completed' },
      ],
      ratingOptions: [
        { value: 'yes', text: 'Send Rating' },
        { value: 'no', text: 'No Rating' },
        { value: 'not_decided', text: 'Not Decided' },
      ],
      callStatusOptions: [
        { value: 'not_contacted', text: 'Not contacted' },
        { value: 'attempted', text: 'Attempted' },
        { value: 'successful', text: 'Successful' },
        { value: 'unreachable', text: 'Unreachable' },
      ],
      satisfactionOptions: [
        { value: 'very_satisfied', text: '1 - Very dissatisfied' },
        { value: 'satisfied', text: '2 - Dissatisfied' },
        { value: 'neutral', text: '3 - Neutral' },
        { value: 'dissatisfied', text: '4 - Satisfied' },
        { value: 'very_dissatisfied', text: '5 - Very satisfied' },
      ],
      internalSatisfaction: [
        { value: 1, text: '1 - Very dissatisfied' },
        { value: 2, text: '2 - Dissatisfied' },
        { value: 3, text: '3 - Neutral' },
        { value: 4, text: '4 - Satisfied' },
        { value: 5, text: '5 - Very satisfied' },
      ],
    };
  },

  computed: {
    // Apply all filters
    filteredData() {
      let data = [...this.allInteractions];
        
      if (this.statusFilter === 'active') {
        data = data.filter(item => 
          item.contact_status === 'pending' || 
          item.contact_status === 'in_progress' ||
          item.contact_status === 'delayed'
        );
      } else if (this.statusFilter !== null) {
        data = data.filter(item => item.contact_status === this.statusFilter);
      }

      // Rating filter
      if (this.ratingFilter !== null) {
        data = data.filter(item => item.rating_request_decision === this.ratingFilter);
      }

      // Search filter
      if (this.searchText && this.searchText.trim()) {
        const search = this.searchText.toLowerCase().trim();
        data = data.filter(item => {
          return (
            (item.customer_name && item.customer_name.toLowerCase().includes(search)) ||
            (item.customer_email && item.customer_email.toLowerCase().includes(search)) ||
            (item.order_number && item.order_number.toString().toLowerCase().includes(search)) ||
            (item.customer_phone && item.customer_phone.toLowerCase().includes(search))
          );
        });
      }

      // Sort
      data.sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      return data;
    },

    // Total after filtering
    filteredTotal() {
      return this.filteredData.length;
    },

    // Calculate total pages
    totalPages() {
      return Math.ceil(this.filteredData.length / this.perPage) || 1;
    },

    // Get current page data
    displayedData() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredData.slice(start, end);
    }
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        const response = await ApiService.get(ENDPOINT + 'interactions');
        
        this.allInteractions = response.data.data || [];
        this.showSnackbar('Data loaded successfully', 'success');
      } catch (error) {
        console.error('Error loading data:', error);
        this.showSnackbar('Error loading data', 'error');
        this.allInteractions = [];
      } finally {
        this.loading = false;
      }
    },

    setStatusFilter(status) {
      this.statusFilter = status;
      this.currentPage = 1;
    },

    setRatingFilter(rating) {
      this.ratingFilter = rating;
      this.currentPage = 1;
    },

    filterData() {
      this.currentPage = 1;
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },

    getSortIcon(field) {
      if (this.sortField !== field) return 'mdi-arrow-down';
      return this.sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    handlePerPageChange() {
      this.currentPage = 1;
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    // Get row number
    getRowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },

    async openView(item) {
      const resp = await ApiService.get(ENDPOINT + 'interactions/' + item.id);
      this.selectedItem = resp.data
      this.viewDialog = true
    },

    async saveInteraction(payload) {
      //console.log('FULL PAYLOAD:', JSON.stringify(payload, null, 2));
      try{

        const resp = await ApiService.put(
          ENDPOINT + `interactions/${payload.id}`, 
          payload
        );

        //console.log('DATA:', resp.data.data);

        const idx = this.allInteractions.findIndex(x => x.id === resp.data.data.id)
        if (idx !== -1) this.$set(this.allInteractions, idx, { ...this.allInteractions[idx], ...resp.data.data })
        Object.assign(this.selectedItem, resp.data.data)
      }catch(e){
        console.error(e);
      }

      for (const product of payload.products) {
        const data = {
          sales_order_item_id: product.order_item_id,
          comments: product.marketing_comments
        };
      
        await ApiService.put(
          ENDPOINT + `interactions/${payload.id}/products/${product.id}/comment`, 
          data
        );
      }
    },

    getStatusClass(status) {
      return {
        'active': 'status-active',
        'pending': 'status-pending',
        'in_progress': 'status-progress',
        'delayed': 'status-delayed',
        'completed': 'status-completed'
      }[status] || '';
    },

    getStatusText(status) {
      return {
        'pending': 'Pending',
        'in_progress': 'In Progress',
        'delayed': 'Delayed',
        'completed': 'Completed'
      }[status] || status;
    },

    getRatingClass(decision) {
      return {
        'yes': 'rating-yes',
        'no': 'rating-no',
        'not_decided': 'rating-pending'
      }[decision] || '';
    },

    getRatingText(decision) {
      return {
        'yes': 'Send Rating',
        'no': 'No Rating',
        'not_decided': 'Not Decided'
      }[decision] || decision;
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    async handleCompleted(result) {
      if (result.data){
        this.selectedItem = { 
          ...this.selectedItem, 
          ...result.data 
        };
      
        const idx = this.allInteractions.findIndex(x => x.id === result.data.id);
        if (idx !== -1) {
          this.$set(this.allInteractions, idx, { 
            ...this.allInteractions[idx], 
            ...result.data 
          });
        }
      }

      this.showSnackbar(result.message, result.type);
    
      // this.viewDialog = false;
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

.inventory-root {
  --bg:          #f5f6f8;
  --bg2:         #ffffff;
  --bg3:         #f0f1f4;
  --border:      #e2e4ea;
  --border2:     #c8ccd6;
  --text:        #374151;
  --text-dim:    #9ca3af;
  --text-bright: #111827;
  --accent:      #2563eb;
  --accent2:     #1d4ed8;
  --danger:      #ef4444;
  --warn:        #f59e0b;
  --success:     #16a34a;

  background: var(--bg);
  color: var(--text);
  font-family: 'IBM Plex Sans', sans-serif;
}

/* Container */
.marketing-interactions-page {
  padding: 24px;
  background: #fafafa;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0;
  letter-spacing: 0.5px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.record-count {
  font-size: 14px;
  color: #666;
}

/* Filters Card */
.filters-card {
  background: white;
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 16px;
}

.search-box {
  position: relative;
  margin-bottom: 16px;
}

.search-icon {
  position: absolute !important;
  right: 10px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: #999 !important;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-color: #1976d2;
}

.filter-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
}

.filter-btn:hover {
  background: #f5f5f5;
}

.filter-btn.active {
  background: #1976d2;
  color: white;
  border-color: #1976d2;
}

.divider {
  width: 1px;
  height: 24px;
  background: #ddd;
}

/* Loading */
.loading-container {
  background: white;
  padding: 80px 24px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  text-align: center;
  color: #666;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.data-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.data-table th.sortable:hover {
  background: #eee;
}

.data-table td {
  padding: 16px;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.data-row:hover {
  background: #fafafa;
}

.col-index {
  width: 50px;
  text-align: center;
  color: #999;
  font-weight: 500;
}

.col-actions {
  width: 100px;
  text-align: center;
}

/* Table Cells */
.order-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-number {
  font-weight: 500;
  color: #333;
}

.customer-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.customer-name {
  font-weight: 500;
  color: #333;
}

.customer-email {
  font-size: 12px;
  color: #999;
}

.contact-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #666;
}

/* Badges */
.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-count {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-status.status-pending {
  background: #fff3e0;
  color: #f57c00;
}

.badge-status.status-progress {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-status.status-delayed {
  background: #ffcbcb;
  color: #f26a6a;
}

.badge-status.status-completed {
  background: #e8f5e9;
  color: #388e3c;
}

.badge-status.status-active {
  background: #e8f5e9;
  color: #3cc512;
}

.badge-rating.rating-yes {
  background: #e8f5e9;
  color: #388e3c;
}

.badge-rating.rating-no {
  background: #ffebee;
  color: #d32f2f;
}

.badge-rating.rating-pending {
  background: #f5f5f5;
  color: #757575;
}

/* Empty State */
.empty-state {
  background: white;
  padding: 80px 24px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  text-align: center;
  color: #999;
}

/* Pagination */
.pagination-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
}

.per-page-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.page-current {
  background: #1976d2;
  color: white;
  border-color: #1976d2;
}

/* Utilities */
.text-center {
  text-align: center;
}

.text-muted {
  color: #999;
}

.mt-4 {
  margin-top: 16px;
}

.mx-2 {
  margin-left: 8px;
  margin-right: 8px;
}
</style>